import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Typography, TextField, FormControl, Select, MenuItem, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import qs from 'qs';
import { useToast } from 'use-toast-mui';
import { getCenterUserAttendHistory } from '../../api/user';
import { getMemberGroups } from '../../api/member-group';
import { pageSize } from '../../constants/constants';
import AnimationPage from '../../pages/AnimationPage';
import Loader from '../loader';
import FilterButtons from '../FilterButtons';
import { FormInputDate } from '../../form-components/FormInputDate';
import { FormInputText } from '../../form-components/FormInputText';
import CommonSearchSubmitButton from '../../form-components/CommonSearchSubmitButton';
import CommonFilterActionButton from '../../form-components/CommonFilterActionButton';
import CommonPagination from '../../form-components/CommonPagination';
import AttendHistoryListTable from './AttendHistoryListTable';
import AttendHistoryStats from './AttendHistoryStats';
import BetaBadge from '../common/BetaBadge';
import useUserAuth from '../../utils/useUserAuth';

// ============================================
// Helpers
// ============================================
const getTodayMidnight = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

// 회원상태 필터 옵션 (백엔드가 한글 라벨로 판정/반환)
const MEMBER_STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: '유효회원', label: '유효회원' },
  { value: '만료임박', label: '만료임박' },
  { value: '사용예정', label: '사용예정' },
  { value: '중지회원', label: '중지회원' },
  { value: '만료회원', label: '만료회원' },
];

const buildApiQuery = (params, userHead, userBranch) => ({
  page: params.page || 1,
  limit: params.limit || 10,
  searchInput: params.searchInput || '',
  ...(params.registeredDateFrom && {
    startDate: dayjs(params.registeredDateFrom).format('YYYY-MM-DD'),
  }),
  ...(params.registeredDateTo && {
    endDate: dayjs(params.registeredDateTo).format('YYYY-MM-DD'),
  }),
  ...(params.memberStatus && { memberStatus: params.memberStatus }),
  ...(params.memberGroupId && { memberGroupId: params.memberGroupId }),
  headBranchId: userHead?.headBranchId,
  branchId: userBranch?.branchId,
});

// ============================================
// Styled Components
// ============================================
/*
 * 조건 세 줄을 한 격자에 담는다 (#94). 줄마다 따로 flex 를 쓰다 보니 라벨 폭은 같아도
 * 줄 간격이 32px 로 벌어지고 아래 버튼만 가운데라 축이 어긋나 보였다.
 * 라벨 칸과 입력 칸을 격자로 고정하면 어긋날 자리가 없다.
 */
const FilterGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '97px 1fr',
  alignItems: 'center',
  rowGap: '14px',
  columnGap: '0',
});

const FieldControl = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
  minWidth: 0,
});

const FieldLabel = styled(Typography)({
  width: '97px',
  fontSize: '16px',
  fontWeight: 500,
});

const SearchInputWrapper = styled(Box)({
  width: '348px',
});



const StatusSelect = styled(Select)({
  width: '160px',
  background: '#fff',
  borderRadius: '5px !important',
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4AB3BC' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4AB3BC' },
});

const GroupSelect = styled(Select)({
  width: '220px',
  background: '#fff',
  borderRadius: '5px !important',
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4AB3BC' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4AB3BC' },
});

const PeriodGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  columnGap: '20px',
});

const DateField = styled(TextField)({
  width: '140px',
  background: '#fff',
  '.MuiInputBase-root': { paddingRight: '0px' },
  '.MuiIconButton-root': { marginRight: '0px' },
  input: { paddingRight: '0px!important' },
  '& .MuiSvgIcon-root': { fill: '#4AB3BC !important' },
});

const DateSeparatorIcon = styled('img')({
  width: 10,
  height: 16,
});

// FormInputDate 내부 input 폰트 크기 보정 (라이브러리 클래스명에 직접 접근)
const datePickerSx = {
  '.css-1x51dt5-MuiInputBase-input-MuiInput-input': {
    fontSize: '16px',
  },
};

const renderDateField = params => <DateField color="info" size="small" variant="outlined" {...params} />;

const SectionDivider = styled(Divider)({
  border: '1px solid #e1e1e1',
  marginTop: '16px',
  marginBottom: '16px',
});

// 목록/통계 탭과 한 줄을 쓴다. 탭은 왼쪽, 검색/초기화 버튼은 가운데 칸에 둔다.
const ActionButtonRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
});

const ActionButtonGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const StatsBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '24px',
  marginBottom: '8px',
});

const StatsGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const StatsLabel = styled(Typography)({
  fontSize: '16px',
  color: '#666',
});

const StatsValue = styled(Typography, {
  shouldForwardProp: prop => prop !== 'highlight',
})(({ highlight }) => ({
  fontWeight: 600,
  fontSize: '16px',
  color: highlight ? '#4AB3BC' : '#333',
  marginLeft: '4px',
}));

const PageSizeSelect = styled(Select)({
  width: '108px',
  borderRadius: '5px !important',
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC',
  },
});

/* 목록/통계 탭 (#94). 검색 조건은 두 탭이 그대로 공유한다. */
const ViewTabs = styled(Box)({
  display: 'flex',
  gap: '4px',
  background: '#F1F4F6',
  borderRadius: '9px',
  padding: '3px',
  width: 'fit-content',
  margin: '4px 0 2px',
});

const ViewTab = styled('button', {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  padding: '6px 18px',
  borderRadius: '7px',
  border: 'none',
  fontSize: '13.5px',
  fontWeight: isActive ? 700 : 600,
  color: isActive ? '#0F8A80' : '#6B7280',
  background: isActive ? '#FFFFFF' : 'transparent',
  boxShadow: isActive ? '0 1px 3px rgba(15,23,42,0.12)' : 'none',
  cursor: 'pointer',
}));

const LoaderRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
});

const TableSection = styled(Box)({
  width: '100%',
});

const NoPermissionBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '80px 0',
});

const NoPermissionText = styled(Typography)({
  fontSize: '16px',
  color: '#999',
});

const Footnote = styled(Typography)({
  margin: '14px 4px 0',
  fontSize: '12.5px',
  color: '#9aa1ab',
});

// ============================================
// Main Component
// ============================================
const AttendHistoryList = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const canView = useUserAuth('ATTEND_VIEW');
  const canExcelDownload = useUserAuth('ATTEND_EXCEL_DOWNLOAD');
  const token = user?.token;
  const userBranch = user?.branch;
  const userHead = user?.headBranch;

  const [resetTrigger, setResetTrigger] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'stats'

  // activeQuery: 실제 API에 전달되는 파라미터. 검색/페이지/리셋 시점에만 갱신됨 (form 변경 시 즉시 반영 X)
  const [activeQuery, setActiveQuery] = useState({
    page: 1,
    limit: 10,
    searchInput: '',
    registeredDateFrom: getTodayMidnight(),
    registeredDateTo: getTodayMidnight(),
    memberStatus: '',
    memberGroupId: '',
  });

  const { handleSubmit, control, reset, setValue, watch, getValues } = useForm({
    defaultValues: {
      searchInput: '',
      registeredDateFrom: getTodayMidnight(),
      registeredDateTo: getTodayMidnight(),
      memberStatus: '',
      memberGroupId: '',
    },
  });

  // 지점 선택 여부 — head는 필수, BRANCH 타입이면 branch도 필수
  const isPointSelected = !!userHead?.headBranchId && (user?.centerType !== 'BRANCH' || !!userBranch?.branchId);

  // 회원 그룹은 지점 안의 자유 그룹이라 지점이 바뀌면 목록도 바뀐다.
  const { data: memberGroups = [] } = useQuery({
    queryKey: ['memberGroups', userBranch?.branchId],
    queryFn: () => getMemberGroups(token, userBranch?.branchId),
    enabled: !!token && !!userBranch?.branchId,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['attendHistory', activeQuery, userHead?.headBranchId, userBranch?.branchId],
    queryFn: async () => {
      if (!isPointSelected) {
        toast.warning(t('Please select a point'));
        return { content: [], totalElements: 0 };
      }
      const queryString = qs.stringify(buildApiQuery(activeQuery, userHead, userBranch));
      return getCenterUserAttendHistory(queryString, token);
    },
    enabled: !!token,
  });

  const statsApiQuery = useMemo(
    () => buildApiQuery(activeQuery, userHead, userBranch),
    [activeQuery, userHead, userBranch],
  );

  const attendList = data?.content || [];
  const totalCount = data?.totalElements || 0;

  const handleSearch = formValues => {
    setActiveQuery(prev => ({ ...prev, ...formValues, page: 1 }));
  };

  // 페이지 변경 시점의 폼 값을 함께 반영 (원본 동작 보존)
  const handleChangePage = (_, value) => {
    setActiveQuery(prev => ({ ...prev, ...getValues(), page: value }));
  };

  const handleChangeLimit = event => {
    setActiveQuery(prev => ({ ...prev, ...getValues(), limit: event.target.value, page: 1 }));
  };

  const handleReset = () => {
    const today = getTodayMidnight();
    const resetValues = {
      searchInput: '',
      registeredDateFrom: today,
      registeredDateTo: today,
      memberStatus: '',
      memberGroupId: '',
    };
    reset(resetValues);
    setActiveQuery({
      page: 1,
      limit: 10,
      ...resetValues,
    });
    setResetTrigger(prev => !prev);
  };

  const submitSearch = handleSubmit(handleSearch);

  return (
    <AnimationPage>
      <Box>
        {/* 검색 조건 — 라벨 칸 97px, 입력 칸 나머지 */}
        <FilterGrid>
          <FieldLabel className="title">{t('Search')}</FieldLabel>
          <FieldControl>
            <SearchInputWrapper>
              <FormInputText
                control={control}
                name="searchInput"
                placeholder={t('내용을 입력해주세요')}
                size="small"
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submitSearch();
                  }
                }}
              />
            </SearchInputWrapper>
            <CommonSearchSubmitButton onClick={submitSearch}>{t('Search')}</CommonSearchSubmitButton>
          </FieldControl>

          <FieldLabel>{t('Period')}</FieldLabel>
          <PeriodGroup>
            <FormInputDate
              name="registeredDateFrom"
              control={control}
              placeholder={t('Select date')}
              sx={datePickerSx}
              renderInput={renderDateField}
            />
            <DateSeparatorIcon src="/images/icons/temp.svg" alt="temp" />
            <FormInputDate
              name="registeredDateTo"
              control={control}
              minDate={watch('registeredDateFrom') || null}
              placeholder={t('Select date')}
              sx={datePickerSx}
              renderInput={renderDateField}
            />
            <FilterButtons setValue={setValue} watch={watch} defaultToToday resetToToday={resetTrigger} />
          </PeriodGroup>

          <FieldLabel>{t('회원상태')}</FieldLabel>
          <Controller
            name="memberStatus"
            control={control}
            render={({ field }) => (
              <FormControl size="small">
                <StatusSelect {...field} displayEmpty>
                  {MEMBER_STATUS_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </StatusSelect>
              </FormControl>
            )}
          />

          <FieldLabel>{t('회원 그룹')}</FieldLabel>
          <Controller
            name="memberGroupId"
            control={control}
            render={({ field }) => (
              <FormControl size="small">
                <GroupSelect {...field} displayEmpty>
                  <MenuItem value="">{t('전체')}</MenuItem>
                  {memberGroups.map(group => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </GroupSelect>
              </FormControl>
            )}
          />
        </FilterGrid>

        <SectionDivider />

        {/* 목록/통계 탭 + 검색/초기화 액션 (한 줄) */}
        <ActionButtonRow>
          <Box>
            {canView && (
              <ViewTabs>
                <ViewTab isActive={view === 'list'} onClick={() => setView('list')}>
                  {t('목록')}
                </ViewTab>
                <ViewTab isActive={view === 'stats'} onClick={() => setView('stats')}>
                  {t('통계')}
                  <BetaBadge />
                </ViewTab>
              </ViewTabs>
            )}
          </Box>
          <ActionButtonGroup>
            <CommonFilterActionButton variant="primary" onClick={submitSearch}>
              {t('Search for members with set conditions')}
            </CommonFilterActionButton>
            <CommonFilterActionButton variant="secondary" onClick={handleReset}>
              {t('Reset search conditions')}
            </CommonFilterActionButton>
          </ActionButtonGroup>
          <Box />
        </ActionButtonRow>

        {/* 권한 없음 안내 또는 테이블 영역 */}
        {!canView ? (
          <NoPermissionBox>
            <NoPermissionText>{t('조회 권한이 없습니다.')}</NoPermissionText>
          </NoPermissionBox>
        ) : (
          <>
            {view === 'stats' ? (
              <AttendHistoryStats apiQuery={statsApiQuery} enabled={isPointSelected} />
            ) : (
              <>
            {/* 통계 + 페이지 사이즈 */}
            <StatsBar>
              <StatsGroup>
                <StatsLabel>
                  전체:
                  <StatsValue component="span" highlight>
                    {isLoading ? t('집계 중...') : `${totalCount}건`}
                  </StatsValue>
                </StatsLabel>
              </StatsGroup>
              <FormControl size="small">
                <PageSizeSelect value={activeQuery.limit} onChange={handleChangeLimit}>
                  {pageSize.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </PageSizeSelect>
              </FormControl>
            </StatsBar>

            {/* 테이블 + 페이지네이션 */}
            {isLoading ? (
              <LoaderRow>
                <Loader />
              </LoaderRow>
            ) : (
              <TableSection>
                <AttendHistoryListTable
                  data={attendList}
                  loading={isLoading}
                  totalCount={totalCount}
                  page={activeQuery.page}
                  pageSize={activeQuery.limit}
                />
                <Footnote>
                  {t('입장횟수가 2회 이상인 회원은 최근 입장이 표시되며, 행을 클릭하면 해당 일자의 입장 기록이 펼쳐집니다.')}
                </Footnote>
                <CommonPagination
                  totalElements={totalCount}
                  limit={activeQuery.limit}
                  page={activeQuery.page}
                  onChange={handleChangePage}
                />
              </TableSection>
            )}
              </>
            )}
          </>
        )}
      </Box>
    </AnimationPage>
  );
};

export default AttendHistoryList;
